import React from "react";
import { formatNumber, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Zap,
  Clock,
  Layers,
  CheckCircle2,
  PlayCircle,
  PauseCircle,
  AlertTriangle,
  Cpu,
} from "lucide-react";
import {
  cleanProcessName,
  formatProcessDuration,
  groupStepsByMachine,
  MachineGroup,
} from "../utils/processPlanningUtils";
import { formatProcessTime } from "../ProcessPlanningList";
import { ProcessStatusBadge } from "./ProcessStatusBadge";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";

interface ProcessMachineKanbanBoardProps {
  items: any[];
  allMachines?: any[];
  onNavigateToOrder?: (poCode: string) => void;
  onNavigateToSalesOrder?: (salesOrderCode: string) => void;
  onOpenLogForm: (item: any) => void;
  onOpenMachineModal: (item: any) => void;
}

export function ProcessMachineKanbanBoard({
  items,
  allMachines = [],
  onNavigateToSalesOrder,
  onOpenLogForm,
  onOpenMachineModal,
}: ProcessMachineKanbanBoardProps) {
  // Nhóm các công đoạn theo từng máy
  const machineGroups: MachineGroup[] = React.useMemo(() => {
    return groupStepsByMachine(items, allMachines);
  }, [items, allMachines]);

  if (machineGroups.length === 0) {
    return (
      <div className="h-48 flex flex-col items-center justify-center gap-2 bg-white rounded-xl border border-slate-200 text-slate-500">
        <Cpu className="w-8 h-8 text-slate-300" />
        <span className="font-semibold text-sm">Không có dữ liệu máy móc nào phù hợp bộ lọc</span>
        <span className="text-xs text-muted-foreground">Thử mở rộng khoảng thời gian hoặc chọn tất cả máy móc</span>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto pb-4 pt-1">
      <div className="flex items-start gap-4 min-w-max px-0.5">
        {machineGroups.map((group) => {
          const isUnassigned = group.machineId === "UNASSIGNED";

          return (
            <div
              key={group.machineId}
              className={cn(
                "w-[330px] rounded-xl border flex flex-col max-h-[calc(100vh-280px)] shadow-2xs transition-all",
                isUnassigned
                  ? "bg-amber-50/50 border-amber-200"
                  : "bg-slate-50/90 border-slate-200"
              )}
            >
              {/* Kanban Column Header */}
              <div
                className={cn(
                  "p-3 border-b flex flex-col gap-1.5 rounded-t-xl select-none sticky top-0 z-10",
                  isUnassigned
                    ? "bg-amber-100/70 border-amber-200 text-amber-900"
                    : "bg-white border-slate-200 text-slate-900"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 truncate">
                    <Cpu className={cn("w-4 h-4 shrink-0", isUnassigned ? "text-amber-600" : "text-teal-600")} />
                    <span className="font-bold text-sm truncate" title={group.machineName}>
                      {group.machineName}
                    </span>
                  </div>
                  <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 shrink-0">
                    {group.items.length} CĐ
                  </span>
                </div>

                {/* Sub-metrics: Tổng giờ tải & tổng sản lượng */}
                <div className="flex items-center justify-between text-[11px] font-mono text-slate-600 pt-0.5">
                  <span className="flex items-center gap-1 text-indigo-700 font-semibold">
                    <Clock className="w-3 h-3 text-indigo-500" />
                    {group.totalDurationHours} giờ máy
                  </span>
                  <span>📦 {formatNumber(group.totalQty)} sp</span>
                </div>
              </div>

              {/* Cards List in Column */}
              <div className="p-2.5 overflow-y-auto space-y-2.5 flex-1 divide-y divide-transparent">
                {group.items.map((item: any) => {
                  const cleanName = cleanProcessName(item.name, item.stepId, item.processGroupName);
                  const durationStr = formatProcessDuration(
                    item.estDurationHours,
                    item.plannedTotalTime,
                    item.plannedSetupTime,
                    item.plannedSpeed
                  );

                  return (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenMachineModal(item)}
                      onKeyDown={(e) => { if (e.key === "Enter") onOpenMachineModal(item); }}
                      className={cn(
                        "bg-white rounded-lg p-3 border transition-all hover:shadow-md space-y-2 text-xs cursor-pointer",
                        item.isOverdue
                          ? "border-rose-300 shadow-rose-50"
                          : "border-slate-200 shadow-2xs hover:border-teal-300"
                      )}
                    >
                      {/* Row 1: Thumbnail + PO Code & Status Badge */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <ProductionThumbnailCell
                            imageUrl={item.productImage}
                            layoutUrl={item.layoutUrl}
                            title={item.productName || item.poCode}
                            subtitle={item.poCode}
                            type="auto"
                            size={36}
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="font-mono font-bold text-teal-700 text-left">
                              {item.poCode || item.productionOrderId.substring(0, 8)}
                            </span>
                            <span className="text-[10px] text-muted-foreground truncate" title={item.customerName}>
                              {item.customerName || "Khách lẻ"}
                            </span>
                          </div>
                        </div>
                        <ProcessStatusBadge status={item.status} />
                      </div>

                      {/* Row 2: Sales Order Info */}
                      {item.salesOrderCode && (
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span className="text-[10px]">Đơn hàng:</span>
                          <span className="font-mono font-semibold text-slate-700">
                            {item.salesOrderCode}
                          </span>
                        </div>
                      )}

                      {/* Row 3: Product Name */}
                      <div className="font-semibold text-slate-800 line-clamp-1" title={item.productName}>
                        {item.productName}
                      </div>

                      {/* Row 4: Clean Step Name (Rule 6) */}
                      <div className="flex items-center gap-1.5 bg-slate-50 p-1.5 rounded border border-slate-100">
                        <span className="px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-slate-200 text-slate-800 shrink-0">
                          B{item.stepOrder}
                        </span>
                        <span className="font-semibold text-slate-900 truncate">
                          {cleanName}
                        </span>
                      </div>

                      {/* Row 5: Schedule & Deadline */}
                      <div className="flex items-center justify-between text-[11px] font-mono">
                        <span className="text-slate-500">
                          Từ: {formatProcessTime(item.plannedStartDate)}
                        </span>
                        <div className="flex items-center gap-1">
                          <span className={cn(item.isOverdue ? "text-rose-600 font-bold" : "text-slate-700")}>
                            Hạn: {formatProcessTime(item.plannedEndDate)}
                          </span>
                          {item.isOverdue && (
                            <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">
                              Trễ
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Row 6: Quantity & Standard duration */}
                      <div className="flex items-center justify-between text-[11px] font-mono bg-indigo-50/60 px-2 py-1 rounded border border-indigo-100">
                        <span className="text-indigo-800 font-bold">
                          {formatNumber(item.standardQuantity)} {item.productionUnit || "tờ"}
                        </span>
                        <span className="text-slate-600 font-medium">
                          Định mức: <strong>{durationStr}</strong>
                        </span>
                      </div>

                      {/* Row 7: Progress Bar */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-[10px] font-mono">
                          <span className="text-emerald-700 font-bold">
                            Đạt: {formatNumber(item.actualQtyOk)}
                          </span>
                          {item.actualQtyNg > 0 && (
                            <span className="text-rose-600 font-semibold">
                              Lỗi: {formatNumber(item.actualQtyNg)}
                            </span>
                          )}
                          <span className="text-slate-500 font-semibold">
                            {item.progressPercent}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all duration-300",
                              item.progressPercent >= 100
                                ? "bg-emerald-500"
                                : item.progressPercent > 50
                                ? "bg-teal-500"
                                : "bg-blue-500"
                            )}
                            style={{ width: `${Math.min(100, item.progressPercent)}%` }}
                          />
                        </div>
                      </div>

                      {/* Row 8: Actions Buttons */}
                      <div
                        className="flex items-center justify-end gap-1.5 pt-1 border-t border-slate-100"
                        onClick={(e) => e.stopPropagation()}
                      >

                        <Button
                          size="sm"
                          className="h-6 px-2.5 text-[11px] font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-2xs flex items-center gap-1 cursor-pointer"
                          onClick={() => onOpenLogForm(item)}
                          title="Ghi nhận sản lượng cho công đoạn này"
                        >
                          <Zap className="w-3 h-3 fill-amber-300 text-amber-300" />
                          <span>Ghi Nhật Ký</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
